import { useEffect, useRef, useCallback } from "react";
import { analytics } from "./analytics";
/**
 * Отслеживает изменения объекта параметров и отправляет событие в аналитику
 * с дебаунсом.
 *
 * @example
 * useParamsTracking(params, { debounceMs: 2000 });
 */
export function useParamsTracking(params, options = {}) {
    const { eventName = "configurator_params_changed", debounceMs = 2000, transform, skipInitial = true, } = options;
    const timerRef = useRef(null);
    const isMounted = useRef(false);
    useEffect(() => {
        // Пропускаем первый рендер
        if (!isMounted.current) {
            isMounted.current = true;
            if (skipInitial)
                return;
        }
        if (timerRef.current)
            clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            const raw = params;
            const payload = transform
                ? transform(raw)
                : defaultTransform(raw);
            analytics.track({ name: eventName, params: payload });
        }, debounceMs);
        return () => {
            if (timerRef.current)
                clearTimeout(timerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);
}
// ─── Хук для ручной отправки событий (кнопки, формы и т.д.) ─────────────────
/**
 * Возвращает функцию `trackEvent` для ручной отправки событий.
 * Стабильна между рендерами (useCallback).
 *
 * @example
 * const { trackEvent } = useAnalytics();
 * trackEvent("configurator_form_open");
 * trackEvent("configurator_lead_submit", { name, phone });
 */
export function useAnalytics() {
    const trackEvent = useCallback((name, params) => {
        analytics.track({ name, params });
    }, []);
    return { trackEvent };
}
// ─── Утилиты ──────────────────────────────────────────────────────────────────
/**
 * Приводит значения к типам, понятным GA4/YM (string | number | boolean | null).
 * Округляет float до 4 знаков. Пропускает функции и объекты.
 */
function defaultTransform(raw) {
    const result = {};
    for (const [key, value] of Object.entries(raw)) {
        if (value === null || value === undefined) {
            result[key] = null;
        }
        else if (typeof value === "number") {
            result[key] = Math.round(value * 10000) / 10000; // 4 знака
        }
        else if (typeof value === "boolean") {
            result[key] = value;
        }
        else if (typeof value === "string") {
            result[key] = value;
        }
        // объекты, массивы, функции — пропускаем
    }
    return result;
}
